import { FlightViewMapLibTrackerHeader } from './header'
import { FlightViewMapLibTrackerTable } from './table'
import { FlightViewMapLibTrackerSocialSharing } from './social-sharing'
import { FlightData, InitProps } from '../types'
import { fvaxSignal } from '../AccessEvent'
import { createElement, differenceInMinutes, sleep } from '../utils'
import { FlightViewMapLibMap } from '../map'
import L from 'leaflet'
import { getFlight } from './api'
import { Flight } from '../models/flight'
import { AirportLayer } from '../models/airport-layer'

const headerElementId: string = 'fvlib-header'
const tableElementId: string = 'fvlib-status'
const socialsContainerId: string = 'fvlib-footer'

export class FlightViewMapLibTracker {
  // submodule
  mapModule: FlightViewMapLibMap
  header: FlightViewMapLibTrackerHeader
  table: FlightViewMapLibTrackerTable
  socialSharing: FlightViewMapLibTrackerSocialSharing

  // init
  custId: string
  toolId: string
  layout: string
  props: InitProps

  // data
  flights: FlightData[]
  autoUpdate = true

  // map
  map: L.Map
  flight: Flight
  airportLayer: AirportLayer
  mapIconPointerSize: number = 20
  replayLayer: L.Layer
  hideList: string[] = []

  constructor (parentElement: HTMLElement, props: InitProps) {
    this.custId = props.custId
    this.toolId = props.toolId
    this.layout = props.tracker?.layout
    const mapContainer = document.createElement('div')
    mapContainer.className = 'fvlib-map-container'
    parentElement.appendChild(mapContainer)
    this.mapModule = new FlightViewMapLibMap(mapContainer, props)
    this.map = this.mapModule.map
    this.flight = new Flight(this.map, 60, props.speedUnit, true)
    this.airportLayer = new AirportLayer(this.map, this.mapIconPointerSize)
    const container = document.createElement('div')
    container.className = 'fvlib-info-container'
    parentElement.appendChild(container)
    container.appendChild(createElement(headerElementId))
    container.appendChild(createElement(tableElementId))
    this.header = new FlightViewMapLibTrackerHeader({ elementId: headerElementId, layout: this.layout })
    this.table = new FlightViewMapLibTrackerTable({
      custId: this.custId,
      elementId: tableElementId,
      weatherUnit: props.weatherUnit !== undefined ? props.weatherUnit : 'F',
      speedUnit: props.speedUnit !== undefined ? props.speedUnit : 'K',
      trackerProps: props.tracker,
      timeFormat: props.dateTimeFormat || props.timeFormat || 'MMM d, h:mm a',
      locale: props.locale || 'en'
    })
    if (props.tracker.iFrameBelowMapHide) {
      this.hideList = props.tracker.iFrameBelowMapHide.split(',')
    }
    if (props.tracker.socialMediaEnabled) {
      this.socialSharing = new FlightViewMapLibTrackerSocialSharing(socialsContainerId, props)
    }
    this.props = props

    // check that parameters are correct: we have a date, and at least either a complete flight id, or departure + arrival airports
    if (((props.tracker.flightNo && props.tracker.airline) || (props.tracker.departureAirport && props.tracker.arrivalAirport)) && props.tracker.departureDate) {
      this.fetchInitialData()
    } else {
      this.mapModule.ready(false)
      return
    }

    const ro = new ResizeObserver(() => {
      if (this.flights) {
        fvaxSignal('FVACCESS_UPDATE', 'resized', this.custId, this.toolId, this.flights[0])
      }
    })

    ro.observe(parentElement)

    this.map.on('layeradd', this.onLayerAdd, this)
    this.map.on('layerremove', this.onLayerRemove, this)
  }

  fetchInitialData () {
    void getFlight(this.props.tracker).then((flights) => {
      void this.parseInitialXml(flights)
    })
  }

  fetchUpdateData () {
    void getFlight(this.props.tracker).then((flights) => {
      this.parseUpdateXml(flights)
    })
  }

  async parseInitialXml (flights: FlightData[]) {
    this.applyInitialFlight(flights)
    if (this.flights && this.flights.length > 0) {
      this.header.update(this.flights)
      await this.table.update(this.flights)
      this.socialSharing?.updateUI(this.flights[0])
      this.mapModule.updateLastUpdated()
      if (!this.flights[this.flights.length - 1].status.includes('InGate')) {
        this.scheduleFlightUpdate()
      } else {
        this.mapModule.cancelUpdate()
      }
      this.showIframeBelowMap(flights)
    }
    fvaxSignal('FVACCESS_INIT', null, this.custId, this.toolId, flights ? flights[0] : null)
  }

  parseUpdateXml (flights: FlightData[]) {
    if (this.areResultsValid(flights)) {
      this.flights = flights
      this.update(this.flights)
      this.mapModule.updateLastUpdated()
      this.header.update(this.flights)
      void this.table.update(this.flights).then(() => {
        this.socialSharing?.updateUI(this.flights[0])
      })
      this.mapModule.updateLastUpdated()
      if (this.flights[this.flights.length - 1].status.includes('InGate')) {
        this.autoUpdate = false
        this.mapModule.cancelUpdate()
      }
    }
  }

  scheduleFlightUpdate () {
    void sleep(40000).then(() => {
      this.fetchUpdateData()
      if (this.autoUpdate) {
        this.scheduleFlightUpdate()
      }
    })
  }

  applyInitialFlight (flights: FlightData[]) {
    if (this.areResultsValid(flights)) {
      this.flights = flights
      void this.applyFlights(flights).then(() => {
        this.replayLayer = L.layerGroup()
        this.mapModule.ready(true, {
          Replay: this.replayLayer
        })
      })
    } else {
      this.mapModule.ready(false)
    }
  }

  /**
   * We display the results only if:
   * - Response contains only one result
   * - Response contains multiple results for the same flight (diversion + recovery)
   *
   * Maybe in the future, we will display all flights in a table for the user to select.
   */
  areResultsValid (flights: FlightData[]) {
    if (flights === null || flights.length === 0) {
      return false
    } else if (flights.length === 1) {
      return true
    } else {
      let id = null
      for (const flight of flights) {
        const candidate = `${flight.flightId.airlineCode}${flight.flightId.flightNumber}`
        if (id === null) {
          id = candidate
        } else if (id !== candidate) {
          return false
        }
      }
      return true
    }
  }

  update (flights: FlightData[]) {
    this.flights = flights
    this.flight.apply(flights)
    if (this.layout && this.layout === '2023v1') {
      this.setPadding(false)
    }
  }

  async applyFlights (flights: FlightData[]) {
    await this.airportLayer.createAirportMarkers(flights)
    this.flight.apply(flights)
    if (this.layout && this.layout === '2023v1') {
      this.setPadding(true)
    }
  }

  private setPadding (checkOffSetHeight: boolean) {
    if (this.flight.plane.path) {
      const bounds = this.flight.plane.path.path.getBounds()
      const infoContainer: HTMLElement = document.getElementsByClassName('fvlib-info-container')[0] as HTMLElement || null
      console.log(`${infoContainer?.offsetHeight} ${infoContainer.clientHeight} ${infoContainer.scrollHeight} ${infoContainer}`)
      const bottomPadding = infoContainer?.offsetHeight || 0

      if (checkOffSetHeight) {
        this.flight.plane.map.fitBounds(bounds, { animate: false, padding: [50, 50], paddingBottomRight: [50, 50 + (bottomPadding < 35 ? 207 : 0)] })
      } else {
        this.flight.plane.map.fitBounds(bounds, { animate: false, padding: [50, 50], paddingBottomRight: [50, 50 + bottomPadding] })
      }
    }
  }

  onLayerAdd (event: L.LayerEvent) {
    if (event.layer === this.replayLayer) {
      this.flight.startReplay()
    }
  }

  onLayerRemove (event: L.LayerEvent) {
    if (event.layer === this.replayLayer) {
      this.flight.stopReplay()
    }
  }

  generateIframeBelowMapSrc (flight: FlightData): string {
    let url = this.props.tracker.iFrameBelowMapSrc
    url = url.replaceAll('&amp;', '&')
    url = url.replace('%AirlineCode%', flight.flightId.airlineCode)
    url = url.replace('%AirlineName%', flight.flightId.airlineName)
    url = url.replace('%FlightNumber%', flight.flightId.flightNumber)
    url = url.replace('%SchedDepartureLocalDate%', flight.departureAirport.scheduled.toISOString().substring(0, 10))
    url = url.replace('%SchedDepartureLocalTime%', flight.departureAirport.scheduled.toISOString().substring(11, 19))
    url = url.replace('%SchedDepartureUtcDate%', flight.departureAirport.utc.scheduled.toISOString().substring(0, 10))
    url = url.replace('%SchedDepartureUtcTime%', flight.departureAirport.utc.scheduled.toISOString().substring(11, 19))
    url = url.replace('%Status%', flight.status[0])
    url = url.replace('%DepartureAirportCode%', flight.departureAirport.code)
    url = url.replace('%DepartureTerminal%', flight.departureAirport.terminal)
    url = url.replace('%DepartureGate%', flight.departureAirport.gate)
    url = url.replace('%ArrivalAirportCode%', flight.arrivalAirport.code)
    url = url.replace('%ArrivalTerminal%', flight.arrivalAirport.terminal)
    url = url.replace('%ArrivalGate%', flight.arrivalAirport.gate)
    url = url.replace('%MinsToArrival%', String(differenceInMinutes(flight.arrivalAirport.utc.time.getTime(), new Date().getTime())))
    return url
  }

  showIframeBelowMap (flights: FlightData[]) {
    if (this.props.tracker.iFrameBelowMapSrc) {
      const element = document.getElementById('iframeBelowMap') as HTMLIFrameElement
      if (this.hideList.length > 0) {
        this.hideList.forEach(hide => {
          if (this.flights[this.flights.length - 1].status.includes(hide)) {
            element.style.display = 'none'
          }
        })
      }
      if (element && element.style.display !== 'none') {
        try {
          element.src = this.generateIframeBelowMapSrc(flights[0])
        } catch (e) {
          console.error('Cannot generate iframe url', e)
        }
      }
    }
  }
}
